import React, { useEffect, useState } from 'react'
import { Link2, Loader2, Play } from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import athleteApi from '@/lib/api'
import type { ThemeExample } from '@adthlete/common/utils/dealPricing'

type Post = ThemeExample & { postedAt: string | null }

const compact = (n: number | null) =>
  n == null
    ? ''
    : n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
      : n >= 1000
        ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
        : String(n)
const platformName = (p: string | null) =>
  p === 'tiktok'
    ? 'TikTok'
    : p === 'youtube'
      ? 'YouTube'
      : p === 'instagram'
        ? 'Instagram'
        : 'Post'
const SOCIAL = /^https?:\/\/([a-z0-9-]+\.)*(instagram\.com|tiktok\.com|youtube\.com|youtu\.be)\//i

/** A small preview of a theme's example, or the button to add one */
export const ThemeExampleRow: React.FC<{
  example: ThemeExample | null | undefined
  onOpen: () => void
}> = ({ example, onOpen }) =>
  example ? (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full min-w-0 items-center gap-2.5 rounded-xl bg-muted/60 p-1.5 pr-3 text-left hover:bg-muted"
    >
      {example.thumbnail ? (
        <img
          src={example.thumbnail}
          alt=""
          className="h-10 w-8 shrink-0 rounded-md object-cover"
          loading="lazy"
        />
      ) : (
        <span className="flex h-10 w-8 shrink-0 items-center justify-center rounded-md bg-card text-muted-foreground">
          <Link2 className="h-4 w-4" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-foreground">
          {example.caption || `${platformName(example.platform)} post`}
        </span>
        <span className="block text-xs text-muted-foreground">
          Example
          {example.views ? ` · ${compact(example.views)} views` : ''} · Change
        </span>
      </span>
    </button>
  ) : (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-xl border border-dashed border-[#9BD85A] px-3 py-2 text-left text-xs font-semibold text-[#3F7A0A] hover:bg-[#F7FEE7]"
    >
      + Add an example post · brands see it as proof
    </button>
  )

/**
 * Pick the post that shows a theme: one of the athlete's recent posts (tap a thumbnail) or a
 * pasted Instagram, TikTok or YouTube link. Brands see it in their Content Shop.
 */
export const ThemeExamplePicker: React.FC<{
  open: boolean
  onOpenChange: (o: boolean) => void
  themeLabel: string
  current: ThemeExample | null | undefined
  onPick: (example: ThemeExample | null) => void
}> = ({ open, onOpenChange, themeLabel, current, onPick }) => {
  const [posts, setPosts] = useState<Post[] | null>(null)
  const [link, setLink] = useState('')
  useEffect(() => {
    if (!open || posts) return
    athleteApi
      .get('/athlete/themes/posts')
      .then((r) => setPosts(r.data?.posts ?? []))
      .catch(() => setPosts([]))
  }, [open, posts])
  useEffect(() => {
    if (open) setLink(current && !current.thumbnail ? current.url : '')
  }, [open, current])

  const choose = (e: ThemeExample | null) => {
    onPick(e)
    onOpenChange(false)
  }
  const linkOk = SOCIAL.test(link.trim())

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="mx-auto flex max-h-[88dvh] max-w-lg flex-col rounded-t-3xl p-0"
      >
        <div className="border-b border-border px-4 pb-3 pt-4">
          <SheetTitle className="text-lg font-bold text-foreground">
            Show brands your “{themeLabel}”
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Pick a post that's a good example. Brands see it next to this series in your Content
            Shop.
          </SheetDescription>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
          {posts == null ? (
            <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your posts…
            </p>
          ) : posts.length ? (
            <ul className="grid grid-cols-3 gap-2">
              {posts.map((p) => {
                const on = current?.url === p.url
                return (
                  <li key={p.url}>
                    <button
                      type="button"
                      onClick={() => {
                        const { postedAt: _p, ...example } = p
                        choose(example)
                      }}
                      aria-pressed={on}
                      aria-label={p.caption ?? `${platformName(p.platform)} post`}
                      className={cn(
                        'relative block aspect-[9/14] w-full overflow-hidden rounded-xl bg-muted',
                        on && 'ring-4 ring-[#C0FD72]',
                      )}
                    >
                      {p.thumbnail ? (
                        <img
                          src={p.thumbnail}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="flex h-full items-center justify-center p-2 text-xs text-muted-foreground">
                          {p.caption ?? platformName(p.platform)}
                        </span>
                      )}
                      <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-[#000000]/70 to-transparent px-2 pb-1.5 pt-4 text-xs font-semibold text-[#FFFFFF]">
                        <Play className="h-3 w-3 fill-current" />
                        {p.views ? compact(p.views) : platformName(p.platform)}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="rounded-xl bg-muted px-3 py-3 text-sm text-muted-foreground">
              No posts to show yet. Connect your Instagram or TikTok and they'll appear here, or
              paste a link below.
            </p>
          )}

          <div className="space-y-2 rounded-2xl border border-border bg-card p-3">
            <p className="text-sm font-semibold text-foreground">Or paste a link to the post</p>
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              inputMode="url"
              placeholder="https://www.instagram.com/reel/…"
              aria-label="Link to the post"
              className="h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40"
            />
            {link.trim() && !linkOk ? (
              <p className="text-xs text-[#B91C1C]">Use an Instagram, TikTok or YouTube link.</p>
            ) : null}
            <button
              type="button"
              disabled={!linkOk}
              onClick={() =>
                choose({
                  url: link.trim(),
                  thumbnail: null,
                  platform: /tiktok/i.test(link)
                    ? 'tiktok'
                    : /youtu/i.test(link)
                      ? 'youtube'
                      : 'instagram',
                  views: null,
                  caption: null,
                })
              }
              className="h-11 w-full rounded-xl bg-[#0F172A] text-sm font-semibold text-[#FFFFFF] disabled:opacity-40"
            >
              Use this link
            </button>
          </div>

          {current ? (
            <button
              type="button"
              onClick={() => choose(null)}
              className="w-full pb-2 text-center text-sm font-semibold text-muted-foreground underline"
            >
              Remove the example
            </button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}
